import { useCallback, useEffect, useMemo, useState } from 'react';
import OrgAdminLayout from '../components/OrgAdminLayout';
import AttendanceClock from '../../components/attendance/AttendanceClock';
import AttendanceMonth, { STATUS, monthOptions } from '../../components/attendance/AttendanceMonth';
import TeamCalendar from '../components/attendance/TeamCalendar';
import LeaveQueue from '../components/attendance/LeaveQueue';
import AttendanceCorrectionModal from '../components/attendance/AttendanceCorrectionModal';
import { apiClient } from '../../auth-lib';
import { Button, Card, Select, Skeleton, Tabs, useToast } from '../../design-system';
import '../styles/Attendance.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

// The firm's view: who's in today, the month's totals per person, and one
// employee's full report — reading the same server-side builder the employee's
// own page uses, so the two can never disagree.

const TABS = [
  { key: 'today', label: 'Today' },
  { key: 'calendar', label: 'Team calendar' },
  { key: 'leave', label: 'Leave requests' },
  { key: 'month', label: 'Monthly totals' },
  { key: 'person', label: 'One employee' },
];

const hhmm = (m) => `${Math.floor(Math.max(0, m) / 60)}h ${String(Math.max(0, Math.round(m)) % 60).padStart(2, '0')}m`;
const clockTime = (d) => (d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function OrgAdminAttendance() {
  const toast = useToast();
  const [tab, setTab] = useState('today');
  const [refreshKey, setRefreshKey] = useState(0);
  // A manager who marks their own day from the layout's dialog is also on this
  // board; without this their row sits stale until they navigate away.
  const revision = useLiveRefresh();

  const [date, setDate] = useState(todayKey());
  const [board, setBoard] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(true);

  const months = useMemo(() => monthOptions(), []);
  const [ym, setYm] = useState(months[0].value);
  const [totals, setTotals] = useState(null);
  const [loadingTotals, setLoadingTotals] = useState(false);

  const [personId, setPersonId] = useState('');
  const [correcting, setCorrecting] = useState(null);

  const loadBoard = useCallback(() => {
    setLoadingBoard(true);
    apiClient
      .get('/api/attendance/team', { params: { date } })
      .then((r) => setBoard(r.data))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load attendance'))
      .finally(() => setLoadingBoard(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, refreshKey, revision]);
  useEffect(loadBoard, [loadBoard]);

  useEffect(() => {
    if (tab !== 'month') return;
    setLoadingTotals(true);
    apiClient
      .get('/api/attendance/team/month', { params: { month: ym } })
      .then((r) => setTotals(r.data))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load totals'))
      .finally(() => setLoadingTotals(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, ym, refreshKey, revision]);

  // Memoised: a fresh [] each render would re-fire the effect below every time.
  const people = useMemo(() => board?.rows || [], [board]);
  useEffect(() => {
    if (!personId && people.length) setPersonId(people[0].userId);
  }, [people, personId]);

  return (
    <OrgAdminLayout title="Attendance" subtitle="Who's in, and how the month is tracking">
      <div className="att-page">
        <Card>
          {/* A manager clocks in too — the control belongs on their page as
              much as on an employee's. */}
          <AttendanceClock onChange={() => setRefreshKey((n) => n + 1)} compact />
        </Card>

        <Tabs tabs={TABS} active={tab} onChange={setTab} />

        {tab === 'today' && (
          <Card>
            <div className="att-head">
              <div>
                <h3 className="att-head-title">
                  {board?.dayLabel ? `${board.dayLabel}` : 'Team attendance'}
                </h3>
                <p className="att-head-sub">
                  {board && `${board.summary.in} in now · ${board.summary.present} present · ${board.summary.absent} absent`}
                </p>
              </div>
              <div className="att-head-actions">
                <label className="att-date">
                  Date <input type="date" value={date} max={todayKey()} onChange={(e) => setDate(e.target.value)} />
                </label>
              </div>
            </div>

            {loadingBoard ? (
              <Skeleton.Text lines={6} />
            ) : (
              <div className="att-scroll">
                <table className="att-table">
                  <thead>
                    <tr>
                      <th>Employee</th><th>Status</th><th>In</th><th>Out</th>
                      <th className="att-num">Worked</th>
                      <th className="att-num">Overtime</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((r) => {
                      const s = STATUS[r.status] || STATUS.upcoming;
                      return (
                        <tr key={r.userId}>
                          <td>
                            <span className="att-name">{r.name}</span>
                            {r.isOpen && <span className="att-dot is-on att-dot-inline" title="Currently clocked in" />}
                          </td>
                          <td><span className={`att-pill ${s.cls}`}>{s.label}</span></td>
                          <td className="att-nowrap">{clockTime(r.firstInAt)}</td>
                          <td className="att-nowrap">{r.isOpen ? '—' : clockTime(r.lastOutAt)}</td>
                          <td className="att-num">{r.workedMinutes ? r.workedLabel : '—'}</td>
                          <td className="att-num">
                            {r.overtimeMinutes ? <b className="att-pos">+{hhmm(r.overtimeMinutes)}</b> : '—'}
                          </td>
                          <td className="att-num">
                            <Button
                              variant="secondary" size="sm"
                              onClick={() => setCorrecting({ userId: r.userId, name: r.name, dateKey: date })}
                            >
                              Correct
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {people.length === 0 && (
                      <tr><td colSpan={7}><p className="att-empty">No active employees.</p></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {tab === 'month' && (
          <Card>
            <div className="att-head">
              <div>
                <h3 className="att-head-title">Monthly totals</h3>
                <p className="att-head-sub">Worked, overtime and balance for every employee</p>
              </div>
              <div className="att-head-actions">
                <Select value={ym} onChange={(e) => setYm(e.target.value)} aria-label="Month">
                  {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Select>
              </div>
            </div>

            {loadingTotals ? (
              <Skeleton.Text lines={6} />
            ) : (
              <div className="att-scroll">
                <table className="att-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th className="att-num">Worked</th>
                      <th className="att-num">Expected</th>
                      <th className="att-num">Balance</th>
                      <th className="att-num">Overtime</th>
                      <th className="att-num">Present</th>
                      <th className="att-num">Absent</th>
                      <th className="att-num">Leave</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(totals?.rows || []).map((r) => (
                      <tr key={r.userId}>
                        <td>
                          <button type="button" className="att-link" onClick={() => { setPersonId(r.userId); setTab('person'); }}>
                            {r.name}
                          </button>
                        </td>
                        <td className="att-num">{r.workedLabel}</td>
                        <td className="att-num att-muted">{hhmm(r.expectedMinutes)}</td>
                        <td className={`att-num ${r.balanceMinutes >= 0 ? 'att-pos' : 'att-neg'}`}>
                          {r.balanceMinutes >= 0 ? '+' : '−'}{hhmm(Math.abs(r.balanceMinutes))}
                        </td>
                        <td className="att-num">{r.overtimeMinutes ? r.overtimeLabel : '—'}</td>
                        <td className="att-num">{r.present}{r.halfDays ? ` (+${r.halfDays}½)` : ''}</td>
                        <td className="att-num">{r.absent || '—'}</td>
                        <td className="att-num">{r.leave || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {tab === 'calendar' && <TeamCalendar />}

        {tab === 'leave' && <LeaveQueue onChanged={() => setRefreshKey((n) => n + 1)} />}

        {tab === 'person' && (
          <>
            <Card>
              <div className="att-head">
                <div>
                  <h3 className="att-head-title">One employee's report</h3>
                  <p className="att-head-sub">The same report the employee sees</p>
                </div>
                <div className="att-head-actions">
                  <Select value={personId} onChange={(e) => setPersonId(e.target.value)} aria-label="Employee">
                    {people.map((p) => <option key={p.userId} value={p.userId}>{p.name}</option>)}
                  </Select>
                </div>
              </div>
            </Card>
            {personId && (
              <AttendanceMonth
                userId={personId}
                refreshKey={refreshKey}
                title={people.find((p) => p.userId === personId)?.name || 'Employee'}
              />
            )}
          </>
        )}
      </div>

      {correcting && (
        <AttendanceCorrectionModal
          {...correcting}
          onClose={() => setCorrecting(null)}
          onSaved={() => { setCorrecting(null); setRefreshKey((n) => n + 1); }}
        />
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminAttendance;
